
import { supabase } from '@/integrations/supabase/client';

/**
 * Fetches client onboarding requests
 */
export const fetchOnboardingRequests = async () => {
  try {
    const { data, error } = await supabase
      .from('client_onboarding')
      .select('*')
      .eq('role', 'client')
      .order('created_at', { ascending: false });

    if (error) {
      throw error;
    }

    console.log('Fetched onboarding requests:', data);
    return { data: data || [] };
  } catch (error) {
    console.error('Error fetching onboarding requests:', error);
    return { data: [], error };
  }
};

/**
 * Updates a client request status
 */
export const updateClientRequestStatus = async (requestId: string, newStatus: string) => {
  try {
    console.log(`Updating request status for ID ${requestId} to ${newStatus}`);
    
    // First, check if the record exists and get its current data
    const { data: checkData, error: checkError } = await supabase
      .from('client_onboarding')
      .select('*')
      .eq('id', requestId)
      .single();
      
    if (checkError) {
      console.error("Error checking if record exists:", checkError);
      throw checkError;
    }
    
    if (!checkData) {
      console.error(`No record found with ID ${requestId}`);
      throw new Error(`No record found with ID ${requestId}`);
    }
    
    console.log("Found existing record:", checkData);
    
    // Map status values for consistency in the database
    let dbStatus = newStatus;
    if (newStatus === 'approved') dbStatus = 'verified';
    if (newStatus === 'pending') dbStatus = 'pending_verification';
    
    // Update the status in the database with a direct approach
    const { data: updatedData, error: updateError } = await supabase
      .from('client_onboarding')
      .update({ 
        status: dbStatus,
        updated_at: new Date().toISOString()
      })
      .eq('id', requestId)
      .select();

    if (updateError) {
      console.error("Error updating status:", updateError);
      throw updateError;
    }
    
    // Prepare the updated record, either from the database response or the verified record
    const updatedRecord = updatedData && updatedData.length > 0 
      ? updatedData[0] 
      : {
          ...checkData,
          status: dbStatus,
          updated_at: new Date().toISOString()
        };
    
    console.log("Update operation completed, updated record:", updatedRecord);
    
    // Only send notification email after successful database update
    if (newStatus === 'approved' || newStatus === 'verified' || newStatus === 'rejected') {
      try {
        // Ensure correct action parameter
        const notifyAction = newStatus === 'approved' ? 'verified' : newStatus;
        
        console.log("Sending notification to user:", updatedRecord);
        const notifyResponse = await supabase.functions.invoke('notify-user', {
          body: { 
            userData: updatedRecord,
            action: notifyAction
          }
        });
        
        console.log("User notification result:", notifyResponse);
        
        if (notifyResponse.error) {
          console.error("Error in notification:", notifyResponse.error);
        }
      } catch (error) {
        console.error("Error sending notification:", error);
      }
    }

    // Return the updated record with the correct status
    return { 
      success: true, 
      updatedRecord: updatedRecord
    };
  } catch (error) {
    console.error('Error updating request status:', error);
    return { success: false, error };
  }
};

/**
 * Updates client information
 */
export const updateClientInfo = async (clientData: any) => {
  try {
    console.log('Updating client information:', clientData);
    
    const { data, error } = await supabase
      .from('client_onboarding')
      .update({ 
        full_name: clientData.fullName,
        email: clientData.email,
        company_name: clientData.companyName || null,
        phone: clientData.phone || null,  // Changed from "phone_number" to "phone"
        updated_at: new Date().toISOString()
      })
      .eq('id', clientData.id)
      .select();
      
    if (error) {
      console.error("Error updating client information:", error);
      throw error;
    }
    
    return { 
      success: true, 
      updatedClient: data && data.length > 0 ? data[0] : null
    };
  } catch (error) {
    console.error('Error updating client information:', error);
    return { success: false, error };
  }
};

/**
 * Toggles the active status of a client
 */
export const toggleClientActiveStatus = async (clientId: string, isActive: boolean) => {
  try {
    console.log(`Toggling active status for client ID ${clientId} to ${isActive}`);
    
    const { data, error } = await supabase
      .from('client_onboarding')
      .update({ 
        is_active: isActive,
        updated_at: new Date().toISOString()
      })
      .eq('id', clientId)
      .select();

    if (error) {
      console.error("Error toggling active status:", error);
      throw error;
    }
    
    return { 
      success: true, 
      updatedRecord: data && data.length > 0 ? data[0] : null
    };
  } catch (error) {
    console.error('Error toggling client active status:', error);
    return { success: false, error };
  }
};

/**
 * Deletes a client and all their data
 */
export const deleteClient = async (clientId: string) => {
  try {
    console.log(`Deleting client with ID ${clientId}`);
    
    // First, check if the record exists
    const { data: checkData, error: checkError } = await supabase
      .from('client_onboarding')
      .select('*')
      .eq('id', clientId)
      .single();
      
    if (checkError) {
      console.error("Error checking if client exists:", checkError);
      throw checkError;
    }
    
    if (!checkData) {
      console.error(`No client found with ID ${clientId}`);
      throw new Error(`No client found with ID ${clientId}`);
    }
    
    console.log("Found client to delete:", checkData);
    
    // Delete the client record
    const { error: deleteError } = await supabase
      .from('client_onboarding')
      .delete()
      .eq('id', clientId);

    if (deleteError) {
      console.error("Error deleting client:", deleteError);
      throw deleteError;
    }
    
    console.log(`Client with ID ${clientId} successfully deleted`);
    
    return { 
      success: true,
      message: `Client with ID ${clientId} successfully deleted`
    };
  } catch (error) {
    console.error('Error deleting client:', error);
    return { success: false, error };
  }
};

/**
 * Adds a new client manually
 */
export const addClient = async (clientData: any) => {
  try {
    console.log('Adding new client:', clientData);
    
    const { data, error } = await supabase
      .from('client_onboarding')
      .insert({
        full_name: clientData.fullName,
        email: clientData.email,
        company_name: clientData.companyName || null,
        phone: clientData.phone || null,
        status: clientData.status || 'pending_verification',
        role: 'client',
        is_active: true
      })
      .select();
      
    if (error) {
      console.error("Error adding client:", error);
      throw error;
    }
    
    return { 
      success: true, 
      client: data && data.length > 0 ? data[0] : null
    };
  } catch (error) {
    console.error('Error adding client:', error);
    return { success: false, error };
  }
};
